import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const ModalOverlay = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  padding: 24px;
  width: 30%;
  max-width: 900px;
  max-height: 50vh;
  overflow: auto;
  position: relative;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 8px;
`;

const ModalBody = styled(Box)`
  position: relative;
  min-height: 200px;
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

type DocumentSignee = 'Landlord' | 'Tenant';

interface Props {
  userId: string;
  documentURL: string;
  signee: DocumentSignee;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onUpdate: (signee: DocumentSignee) => Promise<void>;
}

export const SignDocumentModal: React.FC<Props> = ({
  userId,
  documentURL,
  signee,
  setShow,
  onUpdate,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (documentURL) {
      setIsLoading(true);
      const signWellEmbed = new (window as any).SignWellEmbed({
        url: documentURL,
        allowDecline: false,
        allowClose: true,
        allowDownload: false,
        events: {
          completed: async () => {
            await onUpdate(signee);
            setShow(false); // Close the modal after signing
          },
          closed: async () => {
            await onUpdate(signee);
            setShow(false); // Close the modal on user exit
          },
          error: async () => {
            // dispatch toast error message
          },
        },
      });

      signWellEmbed.open();
      setIsLoading(false);
      return () => {
        signWellEmbed.close(); // Cleanup on unmount
      };
    }
  }, [documentURL, signee, onUpdate, setShow]);

  const handleClose = () => {
    setShow(false);
  };

  return (
    <ModalOverlay
      open={isLoading}
      onClose={handleClose}
      aria-labelledby='sign-document-modal'
      aria-describedby='modal-to-sign-lease-document'
    >
      <ModalContent>
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
        <ModalBody>
          {isLoading ? (
            <LoadingWrapper>
              <CircularProgress size='30px' />
              <Typography variant='body1' sx={{ ml: 2 }}>
                Preparing document for signing...
              </Typography>
            </LoadingWrapper>
          ) : null}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};
